import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';
import { PersonaService } from 'app/core/auth/persona.service';
import { homeFor } from 'app/core/auth/persona-mapping';

/**
 * RoleGuard. SPEC-web-planner-007 AC-17: both this guard and PersonaGuard now read
 * the persona from the same source (PersonaService, i.e. User/Me), so they can
 * never disagree and bounce a user between them.
 *  - SuperAdmin role on the selected workspace's own entry: redirected (UrlTree) to
 *    /admin/subscription (existing behaviour), which PersonaGuard allows. A
 *    SuperAdmin elsewhere who is an ordinary member here is NOT redirected.
 *  - SuperAdmin with no entry in the workspace: allowed through (PersonaGuard rules).
 *  - anyone else with no qualifying entry (non-member, Pending, unknown role):
 *    redirected to /tenant-selection instead of rendering a broken page.
 *  - otherwise allowed.
 */
@Injectable({
    providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild, CanLoad
{
    /**
     * Constructor
     */
    constructor(
        private _personaService: PersonaService,
        private _router: Router
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Can activate
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree>
    {
        return this._check();
    }

    /**
     * Can activate child
     */
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree>
    {
        return this._check();
    }

    /**
     * Can load
     */
    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree>
    {
        return this._check();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _check(): Observable<boolean | UrlTree>
    {
        return this._personaService.resolve().pipe(
            map(({ persona, superAdmin, member, superAdminHere, failed, unauthorized }) => {
                if ( unauthorized ) { return false; } // the interceptor signs out and goes to /sign-in
                if ( failed ) { return true; }        // User/Me failed: the backend remains the authority
                // dev behaviour: only a SuperAdmin role on THIS workspace's own entry redirects
                if ( superAdminHere )
                {
                    return this._router.parseUrl(homeFor(persona, superAdminHere)); // single source: /admin/subscription
                }
                if ( superAdmin )
                {
                    return true;
                }
                return member ? true : this._router.parseUrl('/tenant-selection');
            })
        );
    }
}
